import { useState, useEffect } from 'react';
import { Paper, Text, Stack, Group, Badge, Accordion, Tooltip, Loader } from '@mantine/core';
import dayjs from 'dayjs';
import { getSolverDecisions } from '../api/prediction-api';
import type { SolverDecision, SolverCandidate } from '../types/prediction-types';

interface SolverDecisionsProps {
  weekStart: string;
}

// Convert snake_case score names to human-readable labels
function formatScoreName(key: string): string {
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Color code based on score total
function getScoreColor(score: number, maxScore: number): string {
  const ratio = score / maxScore;
  if (ratio >= 0.9) return 'green';
  if (ratio >= 0.7) return 'teal';
  if (ratio >= 0.5) return 'blue';
  if (ratio >= 0.3) return 'gray';
  return 'red';
}

function ScoreBreakdown({ candidate, maxScore }: { candidate: SolverCandidate; maxScore: number }) {
  // SAFETY: Get score keys dynamically - don't hardcode
  const scoreKeys = candidate.scores ? Object.keys(candidate.scores) : [];
  
  return (
    <Stack gap="xs">
      <Group gap="xs">
        <Text size="sm" fw={600}>{candidate.employee_name}</Text>
        <Badge 
          size="sm" 
          color={getScoreColor(candidate.score_total, maxScore)}
          variant={candidate.is_assigned ? 'filled' : 'light'}
        >
          {candidate.score_total.toFixed(1)}
        </Badge>
        {candidate.is_assigned && <Badge size="xs" color="green">✓ Assigned</Badge>}
      </Group>
      
      <Stack gap={4} ml="md">
        {scoreKeys.map((key) => {
          const scoreData = candidate.scores[key];
          if (!scoreData) return null;
          
          return (
            <Tooltip
              key={key}
              label={`Context: ${typeof scoreData.context === 'number' 
                ? scoreData.context.toFixed(2) 
                : scoreData.context}`}
              withArrow
            >
              <Group gap="xs">
                <Text size="xs" c="dimmed" style={{ minWidth: 120 }}>
                  {formatScoreName(key)}:
                </Text>
                <Badge size="xs" variant="light">
                  {scoreData.value.toFixed(1)}
                </Badge>
                <Text size="xs" c="dimmed">
                  {typeof scoreData.context === 'number' 
                    ? `(${scoreData.context.toFixed(2)})`
                    : `(${scoreData.context})`}
                </Text>
              </Group>
            </Tooltip>
          );
        })}
      </Stack>
    </Stack>
  );
}

export default function SolverDecisions({ weekStart }: SolverDecisionsProps) {
  const [decisions, setDecisions] = useState<SolverDecision[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchDecisions() {
      try {
        setLoading(true);
        setError(null);
        const response = await getSolverDecisions({ week_start: weekStart });
        setDecisions(response.decisions || []);
      } catch (err) {
        console.error('Error fetching solver decisions:', err);
        const errorMessage = err instanceof Error ? err.message : String(err);
        
        // Don't show error if endpoint doesn't exist (404) - just hide component
        if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
          setDecisions([]);
        } else {
          setError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    }

    fetchDecisions();
  }, [weekStart]);

  if (loading) {
    return (
      <Group gap="xs" mt="sm">
        <Loader size="xs" />
        <Text size="xs" c="dimmed">Loading solver decisions...</Text>
      </Group>
    );
  }

  if (error) {
    return (
      <Paper p="md" withBorder bg="red.0">
        <Text size="sm" c="red">Error: {error}</Text>
      </Paper>
    );
  }

  if (decisions.length === 0) {
    // Return null to hide component entirely when no data (e.g., endpoint doesn't exist)
    return null;
  }

  return (
    <Paper p="md" withBorder bg="blue.0">
      <Text size="sm" fw={600} mb="md">
        Solver Decisions (Why each employee was assigned)
      </Text>
      
      <Accordion variant="separated">
        {decisions.map((decision, idx) => {
          // SAFETY: Check candidates is array before processing
          if (!Array.isArray(decision.candidates) || decision.candidates.length === 0) {
            console.warn('Decision missing candidates array:', decision);
            return null;
          }
          
          // Find max score for color coding
          const maxScore = Math.max(...decision.candidates.map(c => c.score_total));
          
          // Sort candidates by score descending
          const sortedCandidates = [...decision.candidates].sort((a, b) => b.score_total - a.score_total);
          
          const assignedCandidate = sortedCandidates.find(c => c.is_assigned);
          
          return (
            <Accordion.Item key={idx} value={`${decision.shift_date}-${decision.start_time}`}>
              <Accordion.Control>
                <Group gap="xs">
                  <Text size="sm" fw={500}>
                    {dayjs(decision.shift_date).format('ddd, MMM D')}
                  </Text>
                  <Badge size="sm" variant="light">
                    {decision.start_time}-{decision.end_time}
                  </Badge>
                  {assignedCandidate && (
                    <>
                      <Text size="sm" c="dimmed">→</Text>
                      <Text size="sm" fw={600} c="green">
                        {assignedCandidate.employee_name}
                      </Text>
                      <Badge size="sm" color="green" variant="light">
                        {assignedCandidate.score_total.toFixed(1)}
                      </Badge>
                    </>
                  )}
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  {sortedCandidates.map((candidate) => {
                    // SAFETY: Check candidate has scores object
                    if (!candidate.scores) {
                      console.warn('Candidate missing scores:', candidate);
                      return null;
                    }
                    
                    return (
                      <ScoreBreakdown
                        key={candidate.employee_id}
                        candidate={candidate}
                        maxScore={maxScore}
                      />
                    );
                  })}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          );
        })}
      </Accordion>
    </Paper>
  );
}
